/**
 * Maps FilterSet + ResourceConfig to Typesense query parameters
 * Converts our DSL to Typesense filter_by and facet_by strings
 */

import type { FilterSet } from '../filter-dsl';
import type { ResourceConfig } from '../resource-config';

/**
 * Period value to days mapping for last-time-registration filter
 */
const PERIOD_TO_DAYS: Record<string, number> = {
  '7-days': 7,
  '14-days': 14,
  '30-days': 30,
  '90-days': 90,
};

/**
 * Helper function to parse period value and calculate cutoff timestamp
 * Rounds to the nearest minute to prevent query cache invalidation on every render
 * @param periodValue - Period value like '7-days', '14-days', etc.
 * @returns Cutoff timestamp in seconds (not milliseconds), or null if invalid
 */
function calculateCutoffTimestamp(periodValue: string): number | null {
  const days = PERIOD_TO_DAYS[periodValue];
  if (!days) {
    console.warn(
      `[calculateCutoffTimestamp] Invalid period value: ${periodValue}`,
    );
    return null;
  }

  // Round current time to nearest minute (60 seconds) to stabilize query cache
  // This prevents constant re-fetching while still updating the filter every minute
  const nowSeconds = Math.floor(Date.now() / 1000 / 60) * 60;
  return nowSeconds - days * 24 * 60 * 60;
}

/**
 * Builds Typesense filter clause for period-based date filters
 * Converts period values (e.g., '7-days', '30-days') to timestamp comparisons
 *
 * @param fieldName - Date field name (e.g., LastTimeRegistrationDate, LastModifiedDate)
 * @param operator - 'is' (date before cutoff) or 'not' (date after cutoff)
 * @param values - Array of period values like ['7-days', '30-days']
 * @returns Typesense filter clause or null if invalid
 */
function buildPeriodBasedDateClause(
  fieldName: string,
  operator: 'is' | 'not',
  values: string[],
): string | null {
  // Since this is typically a single-select filter, there should only be one value
  // But we defensively handle multiple values by using the first valid one
  const cutoff = calculateCutoffTimestamp(values[0]);

  if (cutoff === null) {
    console.warn(
      `[buildPeriodBasedDateClause] Invalid period value:`,
      values[0],
    );
    return null;
  }

  // "is" operator: field < cutoff (date is BEFORE cutoff - e.g., inactive/stale)
  // "not" operator: field >= cutoff (date is AFTER cutoff - e.g., active/recent)
  const comparison = operator === 'is' ? '<' : '>=';

  return `${fieldName}:${comparison}${cutoff}`;
}

/**
 * Converts a FilterSet to Typesense filter_by string.
 * Clauses are ANDed together with &&.
 *
 * Examples:
 * - customer:is(maersk) → CustomerName:=[maersk]
 * - status:is(active,paused) → StatusName:=[active,paused]
 * - internal:not(true) → IsInternalProject:!=[true]
 * - Multiple: CustomerName:=[maersk] && StatusName:=[active,paused]
 * - last-time-registration:is(7-days) → LastTimeRegistrationDate:<[timestamp]
 *
 * @param config - ResourceConfig for the current resource
 * @param filterSet - Parsed FilterSet from URL
 * @returns Typesense filter_by string
 */
export function toFilterBy(
  config: ResourceConfig,
  filterSet: FilterSet,
): string {
  const clauses: string[] = [];

  for (const clause of filterSet) {
    const facetConfig = config.facets[clause.facet];

    if (!facetConfig) {
      // Facet not in config, skip
      continue;
    }

    // Skip filters with no values (empty filters kept in UI for editing)
    if (clause.values.length === 0) {
      continue;
    }

    // Special handling for period-based date filters
    if (facetConfig.isPeriodBasedFilter) {
      const timestampClause = buildPeriodBasedDateClause(
        facetConfig.field,
        clause.operator,
        clause.values,
      );
      if (timestampClause) {
        clauses.push(timestampClause);
      }
      continue;
    }

    const fieldName = facetConfig.field;
    const operator = clause.operator === 'is' ? ':=' : ':!=';

    // Encode values based on field type
    const encodedValues = clause.values.map((v) =>
      encodeValue(v, facetConfig.type),
    );

    // Build Typesense clause
    const typesenseClause = `${fieldName}${operator}[${encodedValues.join(',')}]`;
    clauses.push(typesenseClause);

    // TODO: Handle includeNullInNot flag
    // If operator is "not" and includeNullInNot is true, we need to also include records
    // where the field is missing. Typesense's "!=" may or may not exclude missing fields.
    // For now, we assume Typesense treats missing as not equal.
    // If this is not the case, implement a sentinel value strategy with a feature flag.
  }

  return clauses.join(' && ');
}

/**
 * Generates Typesense facet_by array from ResourceConfig.
 * Includes all facets where facetBy = true.
 *
 * @param config - ResourceConfig for the current resource
 * @returns Array of field names for facet_by
 */
export function toFacetBy(config: ResourceConfig): string[] {
  return Object.values(config.facets)
    .filter((facet) => facet.facetBy)
    .map((facet) => facet.facetField || facet.field);
}

/**
 * Generates Typesense query_by string from ResourceConfig.
 * Returns a comma-separated list of fields to search in when user types in the search box.
 * This is required by Typesense even for wildcard queries (q: '*').
 *
 * Uses config.searchFields if specified, otherwise falls back to 'Name'.
 * searchFields should contain semantically meaningful text fields (e.g., Name, Number, Description),
 * NOT facet fields (which are for filtering, not text search).
 *
 * @param config - ResourceConfig for the current resource
 * @returns Comma-separated field names for query_by
 */
export function toQueryBy(config: ResourceConfig): string {
  // Use explicitly defined searchFields if available
  if (config.searchFields && config.searchFields.length > 0) {
    return config.searchFields.join(',');
  }

  // Fall back to 'Name' as default
  // Most Typesense collections have a Name field
  return 'Name';
}

/**
 * Encodes a value for Typesense based on field type.
 * - string: preserve as-is (slugs are URL-safe: hyphens/underscores allowed)
 * - bool: "true" or "false" as-is
 * - number: parse to number (not used in v1)
 * - date: ISO string (not used in v1)
 *
 * Note: Commas, parentheses, and pipes are not allowed in slugs per our DSL.
 * We don't need to escape them here since they're filtered at parse time.
 *
 * @param value - String value from FilterClause
 * @param type - Field type from FacetConfig
 * @returns Encoded value for Typesense
 */
function encodeValue(value: string, type: string): string {
  switch (type) {
    case 'bool':
      // Boolean values: "true" or "false"
      return value;

    case 'int64':
    case 'int32':
    case 'number':
      // Numeric values: pass through as-is (already stringified)
      return value;

    case 'date':
      // ISO date string (not used in v1, but included for completeness)
      return value;

    case 'string':
    case 'string[]':
    default:
      // String values: preserve hyphens, underscores, alphanumerics
      // Typesense expects string values without quotes in filter arrays
      return value;
  }
}
